import { useQuery } from '@apollo/client';

import User from 'js/lib/user';

import ProgramMembershipsQuery from 'bundles/program-home/queries/ProgramMembershipsQuery.graphql';
import type {
  ProgramMembershipsQueryQuery,
  ProgramMembershipsQueryQueryVariables,
} from 'bundles/program-home/queries/__generated__/ProgramMembershipsQuery';
import { newProgramMembershipInstance } from 'bundles/program-home/types/ProgramMembershipAdapter';

/**
 * Looks up membership for the one programId the learner originally clicked into (preserved
 * through the program->org redirect in EnterprisePageGuard). The bulk org-home membership query
 * never surfaces allowlist-only eligibility (no invitation/roster row backs it), so this reuses
 * the same per-program query + adapter the program-scoped page already relies on for that check.
 */
const useIncomingProgramAllowlistEligibility = (programId?: string) => {
  const userId = User.get().id;
  const { data, loading } = useQuery<ProgramMembershipsQueryQuery, ProgramMembershipsQueryQueryVariables>(
    ProgramMembershipsQuery,
    {
      variables: { programMembershipIds: [{ userId, programId: programId || '' }] },
      context: { clientName: 'gatewayGql' },
      skip: !programId || !userId,
    }
  );

  const programMembershipData = data?.ProgramMemberships?.queryProgramMemberships?.edges?.[0]?.node;
  const programMembership = newProgramMembershipInstance(programMembershipData);

  return {
    isAllowlisted: programMembership.isAllowlisted,
    isEmailNotVerified: programMembership.isEmailNotVerified,
    isLoading: loading,
  };
};

export default useIncomingProgramAllowlistEligibility;
